import { BuildingIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import useSupplier from 'src/api/accounts-payable/useSupplier';
import { useCompanyBySupplier } from 'src/api/core/companies/useCompanyBySupplier';

/**
 * Pure display component — no data fetching.
 * Use this when you already have the supplier/company data (e.g. from a bulk fetch).
 */
export interface SupplierPillDisplayProps {
  name: string;
  companyId?: string;
}

export const SupplierPillDisplay: React.FC<SupplierPillDisplayProps> = ({
  name,
  companyId,
}) => {
  const gt = useGT();

  const handlePillClick = (e: React.MouseEvent<HTMLElement>) => {
    if (companyId) {
      e.stopPropagation();
      e.preventDefault();
      window.open(`/companies/${companyId}`, '_blank');
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const handleAuxClick = (e: React.MouseEvent<HTMLElement>) => {
    if (companyId && e.button === 1) {
      e.stopPropagation();
      e.preventDefault();
      window.open(`/companies/${companyId}`, '_blank');
    }
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      onClick={handleContainerClick}
      sx={{ cursor: 'default' }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        gap={1}
        onClick={handlePillClick}
        onAuxClick={handleAuxClick}
        title={
          companyId
            ? gt('Click to open company details in new tab', {
                context: 'tooltip on supplier pill when linked to company',
              })
            : undefined
        }
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          color: 'info.main',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
          cursor: companyId ? 'pointer' : undefined,
          transition: 'all 0.2s ease-in-out',
          ...(companyId && {
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.16)',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
          }),
        }}
      >
        <BuildingIcon size={16} />
        <MDTypography color="info.main" sx={{ fontSize: 12 }}>
          {name}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

/**
 * Self-fetching wrapper — fetches supplier & company data internally.
 * Use this when you only have a supplierId or basic supplier object.
 */
interface SupplierPillProps {
  supplier?: {
    id: string;
    name: string;
    contactName?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  supplierId?: string;
  companyName?: string;
}

const SupplierPill: React.FC<SupplierPillProps> = ({
  supplier: providedSupplier,
  supplierId: supplierIdProp,
  companyName: providedCompanyName,
}) => {
  const gt = useGT();
  const supplierId = supplierIdProp || providedSupplier?.id;

  const { data: supplierData, isLoading } = useSupplier(
    supplierId,
    undefined,
    !!supplierId && !providedSupplier,
  );
  const { data: company } = useCompanyBySupplier(
    supplierId,
    !!supplierId && !providedCompanyName,
  );

  const supplier = (providedSupplier ||
    supplierData?.supplier) as SupplierPillProps['supplier'];

  const displayName =
    providedCompanyName ||
    company?.name ||
    supplier?.name ||
    gt('Unknown', { context: 'fallback supplier or company name' });

  if ((isLoading && !providedSupplier) || !supplier) {
    return (
      <MDBox
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
        }}
      >
        <MDTypography color="info.main" sx={{ fontSize: 12 }}>
          <T context="loading supplier pill">Loading...</T>
        </MDTypography>
      </MDBox>
    );
  }

  return <SupplierPillDisplay name={displayName} companyId={company?.id} />;
};

export default SupplierPill;
